/**
 * MH-13 Tạo bản tin từ văn bản (mục 5.3 đặc tả giao diện).
 *
 * Nhận văn bản, chọn giọng đọc và tốc độ, tạo BẢN NGHE THỬ chỉ tổng hợp 200 ký
 * tự đầu (để chọn giọng nhanh, không tốn tài nguyên tổng hợp toàn văn). Sau khi
 * chọn giọng, tổng hợp toàn văn là công việc NỀN: đăng ký vào sổ công việc nền
 * dùng chung (`cong-viec-nen.ts`) để người dùng rời màn hình được và được báo
 * khi xong, kết quả trở thành một đoạn trong cấu tạo âm thanh ở MH-12.
 *
 * Bố cục theo trang 'soan-ban-tin' của bản mẫu CMS (loại "Chuyển văn bản thành
 * giọng đọc"): thanh bước, form hai cột trong khung `--form-panel-bg`.
 *   - Mở từ ô chọn loại (không có `banTinId`): cột trái là thông tin bản tin;
 *     "Tạo bản tin" gọi `POST /api/v1/bulletins` (loại `1` — văn bản chuyển giọng
 *     nói, hợp đồng gộp kho 29/10/2026) rồi gửi tổng hợp toàn văn vào bản tin vừa
 *     tạo, sang bước 3 "Hoàn thành". Ô "Loại bản tin" cố định Âm thanh.
 *   - Mở từ MH-12 (`?banTinId=`): cột trái nêu bản tin đích; tổng hợp toàn văn
 *     thêm đoạn vào bản tin đó như luồng cũ.
 * Trường mà API chưa nhận (Quy trình, Lĩnh vực cấp 2, Vùng phát, Địa bàn chia
 * sẻ) khoá và gắn "Chưa có API".
 *
 * HAI KHOẢNG TRỐNG API đã ghi vào NOTES-A0.md mục Z2-B4 (mục B4-10, B4-11):
 *   1. `GET /api/v1/tts/jobs/:id` trả thêm `urlNgheThu` (link nghe qua gateway) khi việc
 *      đã xong — bản nghe thử phát ngay bằng `TrinhPhatAmThanh`.
 *   2. Không có danh mục giọng đọc nào từ máy chủ; danh sách giọng dưới đây là
 *      lấy từ `GET /api/v1/tts/voices` (Viettel AI: 16 giọng, mã gửi thẳng sang
 *      worker). Tốc độ đọc cố định 1.0 (khách bỏ ô chọn tốc độ).
 *
 * Bảng thay thế cách đọc: bảng `bang_thay_the_cach_doc` (migration 025, đợt
 * B2) CHƯA có điểm cuối API nào (không GET, không POST) — mục B4-12. Màn hình
 * vẫn hiện thực bảng này vì đặc tả bắt buộc ("không có bảng này, chất lượng đọc
 * tên địa danh sẽ không đạt yêu cầu"), áp dụng phép thay thế NGAY TRÊN TRÌNH
 * DUYỆT vào văn bản trước khi gửi đi tổng hợp — nhưng KHÔNG lưu lại giữa các
 * phiên (không có server lưu), nêu rõ trong giao diện để người dùng không hiểu
 * lầm là đã lưu theo phạm vi tỉnh như đặc tả yêu cầu.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { dienVao, vi } from '../ngon-ngu/vi';
import { viBienTapNoiDung as t } from '../ngon-ngu/vi-bien-tap';
import { banTinApi } from '../api/ban-tin-api';
import { themCongViecNen } from '../trang-thai/cong-viec-nen';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import { BuocSoanBanTin, HoanThanhBanTin, TruongDiaBanChiaSe, TruongSoan } from './MH12BienTapBanTin';
import { TrinhPhatAmThanh } from '../thanh-phan/TrinhPhatAmThanh';
import './bien-tap.css';

const DO_DAI_NGHE_THU = 200;

/** Danh sách dự phòng khi chưa tải được danh mục giọng từ máy chủ (`GET /api/v1/tts/voices`). */
const GIONG_DOC_DU_PHONG = [
  { ma: 'hn-quynhanh', ten: 'Quỳnh Anh chất lượng cao', moTa: 'Nữ miền Bắc', vung: 'BAC' },
];

interface DongCachDoc {
  id: string;
  tu: string;
  cachDoc: string;
}

/** Thay thế TRÊN TRÌNH DUYỆT trước khi gửi đi tổng hợp — xem ghi chú đầu tệp. */
function apDungCachDoc(vanBan: string, bang: DongCachDoc[]): string {
  let kq = vanBan;
  for (const d of bang) {
    if (!d.tu.trim()) continue;
    const re = new RegExp(`\\b${d.tu.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
    kq = kq.replace(re, d.cachDoc);
  }
  return kq;
}

export interface MH13Props {
  api?: Pick<typeof banTinApi, 'taoTts' | 'chiTietTts' | 'doanAmThanh'> &
    Partial<Pick<typeof banTinApi, 'tao' | 'giongDoc'>>;
}

export function MH13TaoTuVanBan({ api }: MH13Props = {}): ReactNode {
  const goi = api ?? banTinApi;
  const goiTao = api?.tao ?? banTinApi.tao;
  const phien = dungPhien();
  const [thamSo] = useSearchParams();
  const banTinIdDich = thamSo.get('banTinId');

  const [vanBan, datVanBan] = useState('');
  const [dsGiong, datDsGiong] = useState<{ ma: string; ten: string; moTa: string }[]>(GIONG_DOC_DU_PHONG);
  const [giongDoc, datGiongDoc] = useState<string>('hn-quynhanh');
  const goiGiong = api ? api.giongDoc : banTinApi.giongDoc;
  useEffect(() => {
    // Danh mục giọng lấy từ máy chủ (Viettel AI có 16 giọng); lỗi thì giữ danh sách dự phòng.
    if (!goiGiong) return undefined;
    const huy = new AbortController();
    goiGiong(huy.signal)
      .then((kq) => {
        if (kq.items.length === 0) return;
        datDsGiong(kq.items);
        datGiongDoc(kq.items.some((g) => g.ma === kq.macDinh) ? kq.macDinh : (kq.items[0]?.ma ?? ''));
      })
      .catch(() => undefined);
    return () => huy.abort();
  }, [goiGiong]);
  // Tốc độ đọc cố định 1.0 (khách bỏ ô chọn tốc độ trên giao diện).
  const tocDo = 1.0;
  const [bang, datBang] = useState<DongCachDoc[]>([{ id: 'd1', tu: 'UBND', cachDoc: 'Uỷ ban nhân dân' }]);

  // Thông tin bản tin (chỉ dùng khi tạo mới, không có bản tin đích).
  const [tieuDe, datTieuDe] = useState('');
  const [loaiLinhVuc, datLoaiLinhVuc] = useState('1');
  const [mucDoUuTien, datMucDoUuTien] = useState('3');
  const [diaBanChiaSe, datDiaBanChiaSe] = useState<string[]>([]);
  const [loiTen, datLoiTen] = useState(false);
  const [loiTao, datLoiTao] = useState<string | null>(null);
  const [dangTao, datDangTao] = useState(false);
  const [banTinDaTao, datBanTinDaTao] = useState<{ id: string } | null>(null);

  const [ngheThuId, datNgheThuId] = useState<string | null>(null);
  const [ngheThuTrangThai, datNgheThuTrangThai] = useState<string | null>(null);
  const [ngheThuThoiLuong, datNgheThuThoiLuong] = useState<number | null>(null);
  const [ngheThuUrl, datNgheThuUrl] = useState<string | null>(null);
  const hoanThanhRef = useRef(false);

  const [dangGuiToanVan, datDangGuiToanVan] = useState(false);
  const [thongBaoToanVan, datThongBaoToanVan] = useState<string | null>(null);

  const taoNgheThu = useCallback(async (): Promise<void> => {
    hoanThanhRef.current = false;
    datNgheThuTrangThai('cho');
    datNgheThuThoiLuong(null);
    datNgheThuUrl(null);
    const vanBanApDung = apDungCachDoc(vanBan, bang).slice(0, DO_DAI_NGHE_THU);
    const kq = await goi.taoTts({ vanBan: vanBanApDung, giongDoc, tocDo });
    datNgheThuId(kq.id);
  }, [goi, vanBan, bang, giongDoc, tocDo]);

  // Thăm dò trạng thái bản nghe thử — CHỈ khi màn hình còn mở (khác việc toàn
  // văn, vốn đăng ký vào sổ công việc nền để chạy cả khi rời màn hình).
  useEffect(() => {
    if (!ngheThuId || hoanThanhRef.current) return;
    const h = setInterval(() => {
      void goi.chiTietTts(ngheThuId).then((kq) => {
        datNgheThuTrangThai(kq.trangThai);
        datNgheThuThoiLuong(kq.thoiLuongGiay);
        datNgheThuUrl(kq.urlNgheThu ?? null);
        if (kq.trangThai === 'thanh_cong' || kq.trangThai === 'that_bai') {
          hoanThanhRef.current = true;
          clearInterval(h);
        }
      });
    }, 1500);
    return () => clearInterval(h);
  }, [ngheThuId, goi]);

  /** Gửi tổng hợp toàn văn; khi xong, đoạn âm thanh được thêm vào `banTinDich` (nếu có). */
  const guiToanVanVao = useCallback(
    async (banTinDich: string | null): Promise<void> => {
      datDangGuiToanVan(true);
      datThongBaoToanVan(null);
      try {
        const vanBanApDung = apDungCachDoc(vanBan, bang);
        const kq = await goi.taoTts({ vanBan: vanBanApDung, giongDoc, tocDo });
        themCongViecNen({ id: kq.id, loai: 'tts', moTa: vi.taoTuVanBan.tieuDe }, async (idViec) => {
          const ct = await goi.chiTietTts(idViec);
          if (ct.trangThai === 'thanh_cong' && banTinDich) {
            // Kết quả trở thành một đoạn trong cấu tạo âm thanh của bản tin đích.
            await goi.doanAmThanh
              .them(banTinDich, { loaiNguon: 'tts', ttsJobId: idViec })
              .catch(() => undefined);
          }
          return ct;
        });
        datThongBaoToanVan(vi.taoTuVanBan.dangTongHop);
      } finally {
        datDangGuiToanVan(false);
      }
    },
    [goi, vanBan, bang, giongDoc, tocDo],
  );

  /** "Tạo bản tin": tạo bản tin âm thanh rồi tổng hợp toàn văn vào bản tin đó. */
  const taoBanTin = async (): Promise<void> => {
    if (!tieuDe.trim()) {
      datLoiTen(true);
      return;
    }
    datLoiTen(false);
    datLoiTao(null);
    datDangTao(true);
    try {
      const kq = await goiTao({
        tieuDe: tieuDe.trim(),
        // Hợp đồng gộp kho (29/10/2026) mục 1: văn bản chuyển giọng nói là loại `1`.
        loaiBanTin: '1',
        loaiLinhVuc,
        mucDoUuTien,
        noiDungTomTat: vanBan.trim().slice(0, 300) || tieuDe.trim(),
        diaBanTao: phien.nguonIdHienTai ?? '',
        diaBanChiaSe,
      });
      await guiToanVanVao(kq.id);
      datBanTinDaTao({ id: kq.id });
    } catch (e: unknown) {
      datLoiTao(dienVao(t.soan.loiLuu, { loi: e instanceof Error ? e.message : '' }));
    } finally {
      datDangTao(false);
    }
  };

  const coVanBan = vanBan.trim().length > 0;

  return (
    <div className="man-hinh" data-man-hinh="MH-13">
      <h1 className="chi-cho-doc-man-hinh">{vi.taoTuVanBan.tieuDe}</h1>
      <BuocSoanBanTin buoc={banTinDaTao ? 3 : 1} />

      {banTinDaTao ? (
        <HoanThanhBanTin
          laTaoMoi
          ten={tieuDe}
          trangThai="tao_moi"
          banTinId={banTinDaTao.id}
          // Đoạn âm thanh chỉ có khi tổng hợp nền xong — trình duyệt sau ở MH-12.
          trinhDuoc={false}
          dangTrinh={false}
          loiTrinh={null}
          ghiChu={t.soan.ttsDangTongHop}
          onTrinh={() => undefined}
        />
      ) : (
        <>
          <p className="nd-loai-noi-dung" data-vung="loai-noi-dung">
            {t.soan.loaiNoiDung}: <b>{t.loaiNoiDungNhan.tts}</b>
          </p>

          <div className="nd-form" data-vung="soan-hai-cot">
            <section className="nd-panel" aria-label={t.soan.buocTao}>
              {banTinIdDich ? (
                <>
                  <p data-vung="ghi-chu-ban-tin-dich">{vi.taoTuVanBan.guiVaoBanTin}</p>
                  <Link className="nut" to={`/ban-tin/${banTinIdDich}/sua`}>
                    {t.soan.moBienTap}
                  </Link>
                </>
              ) : (
                <>
                  <TruongSoan nhan={t.soan.tenBanTin} htmlFor="tvb-ten" batBuoc>
                    <input
                      id="tvb-ten"
                      placeholder={t.soan.tenBanTinGoiY}
                      maxLength={100}
                      required
                      aria-invalid={loiTen || undefined}
                      aria-describedby={loiTen ? 'tvb-ten-loi' : undefined}
                      value={tieuDe}
                      onChange={(e) => datTieuDe(e.target.value)}
                    />
                    {loiTen ? (
                      <span id="tvb-ten-loi" className="nd-loi" role="alert">
                        {t.soan.tenBatBuoc}
                      </span>
                    ) : null}
                  </TruongSoan>
                  <div className="nd-g2">
                    <TruongSoan nhan={t.soan.linhVucCap1} htmlFor="tvb-lv1">
                      <select
                        id="tvb-lv1"
                        value={loaiLinhVuc}
                        onChange={(e) => datLoaiLinhVuc(e.target.value)}
                      >
                        {(Object.keys(vi.banTin.linhVucNhan) as (keyof typeof vi.banTin.linhVucNhan)[]).map(
                          (k) => (
                            <option key={k} value={k}>
                              {vi.banTin.linhVucNhan[k]}
                            </option>
                          ),
                        )}
                      </select>
                    </TruongSoan>
                    <TruongSoan nhan={t.soan.mucDoUuTien} htmlFor="tvb-uu-tien">
                      <select
                        id="tvb-uu-tien"
                        value={mucDoUuTien}
                        onChange={(e) => datMucDoUuTien(e.target.value)}
                      >
                        {Object.entries(t.uuTienNhan).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v}
                          </option>
                        ))}
                      </select>
                    </TruongSoan>
                    <TruongSoan nhan={t.soan.nguonTin} htmlFor="tvb-nguon-tin">
                      <input id="tvb-nguon-tin" disabled readOnly value={phien.nguonIdHienTai ?? ''} />
                    </TruongSoan>
                    <TruongSoan nhan={t.soan.loaiBanTin} htmlFor="tvb-loai-ban-tin">
                      <select id="tvb-loai-ban-tin" disabled value="am-thanh" onChange={() => undefined}>
                        <option value="am-thanh">{t.nhomLoaiNhan['am-thanh']}</option>
                      </select>
                    </TruongSoan>
                    <TruongSoan nhan={t.soan.vungPhat} htmlFor="tvb-vung-phat">
                      <select id="tvb-vung-phat" disabled value="" onChange={() => undefined}>
                        <option value="">{t.soan.vungPhatMacDinh}</option>
                      </select>
                      <span className="truong__mo-ta">{t.soan.vungPhatGhiChu}</span>
                    </TruongSoan>
                  </div>
                  <TruongDiaBanChiaSe tienTo="tvb" giaTri={diaBanChiaSe} onDoi={datDiaBanChiaSe} />
                </>
              )}
            </section>

            <section className="nd-panel" aria-label={vi.taoTuVanBan.tieuDe}>
              <div className="nd-g2">
                <TruongSoan nhan={vi.taoTuVanBan.nhanGiongDoc} htmlFor="tvb-giong-doc">
                  <select id="tvb-giong-doc" value={giongDoc} onChange={(e) => datGiongDoc(e.target.value)}>
                    {dsGiong.map((g) => (
                      <option key={g.ma} value={g.ma}>
                        {g.moTa ? `${g.ten} - ${g.moTa}` : g.ten}
                      </option>
                    ))}
                  </select>
                </TruongSoan>
              </div>

              <TruongSoan nhan={vi.taoTuVanBan.nhanVanBan} htmlFor="tvb-van-ban" batBuoc>
                <textarea
                  id="tvb-van-ban"
                  rows={10}
                  value={vanBan}
                  onChange={(e) => datVanBan(e.target.value)}
                />
              </TruongSoan>

              <div className="nd-thao-tac">
                <button type="button" className="nut" disabled={!coVanBan} onClick={() => void taoNgheThu()}>
                  {vi.taoTuVanBan.taoNgheThu}
                </button>
                <button
                  type="button"
                  className="nut"
                  disabled={!coVanBan || dangGuiToanVan}
                  onClick={() => void guiToanVanVao(banTinIdDich)}
                >
                  {vi.taoTuVanBan.tongHopToanVan}
                </button>
              </div>

              {ngheThuTrangThai ? (
                <p data-vung="trang-thai-nghe-thu">
                  {ngheThuTrangThai === 'cho' || ngheThuTrangThai === 'dang_xu_ly'
                    ? vi.taoTuVanBan.dangTaoNgheThu
                    : ngheThuTrangThai === 'thanh_cong'
                      ? dienVao('{tieuDe} — {giay}s', {
                          tieuDe: vi.taoTuVanBan.xemTruoc200,
                          giay: ngheThuThoiLuong ?? '?',
                        })
                      : vi.taoTuVanBan.loiTongHop}
                </p>
              ) : null}
              {ngheThuTrangThai === 'thanh_cong' && ngheThuUrl ? (
                <div data-vung="trinh-phat-nghe-thu">
                  <TrinhPhatAmThanh nhan={vi.taoTuVanBan.xemTruoc200} url={ngheThuUrl} />
                </div>
              ) : null}

              {thongBaoToanVan ? (
                <p role="status" data-vung="thong-bao-toan-van">
                  {thongBaoToanVan}
                </p>
              ) : null}

              <section aria-label={vi.taoTuVanBan.bangCachDoc} data-vung="bang-cach-doc">
                <h2 className="nd-tieu-de-phu">{vi.taoTuVanBan.bangCachDoc}</h2>
                <p className="bien-tap__ghi-chu">{vi.taoTuVanBan.bangCachDocMoTa}</p>
                <table className="bang">
                  <thead>
                    <tr>
                      <th scope="col">{vi.taoTuVanBan.tuGoc}</th>
                      <th scope="col">{vi.taoTuVanBan.cachDoc}</th>
                      <th scope="col" />
                    </tr>
                  </thead>
                  <tbody>
                    {bang.map((d, i) => (
                      <tr key={d.id}>
                        <td>
                          <label className="chi-cho-doc-man-hinh" htmlFor={`tvb-tu-${d.id}`}>
                            {vi.taoTuVanBan.tuGoc}: {vi.taoTuVanBan.themDong} {i + 1}
                          </label>
                          <input
                            id={`tvb-tu-${d.id}`}
                            value={d.tu}
                            onChange={(e) =>
                              datBang((ds) =>
                                ds.map((x) => (x.id === d.id ? { ...x, tu: e.target.value } : x)),
                              )
                            }
                          />
                        </td>
                        <td>
                          <label className="chi-cho-doc-man-hinh" htmlFor={`tvb-doc-${d.id}`}>
                            {vi.taoTuVanBan.cachDoc}: {vi.taoTuVanBan.themDong} {i + 1}
                          </label>
                          <input
                            id={`tvb-doc-${d.id}`}
                            value={d.cachDoc}
                            onChange={(e) =>
                              datBang((ds) =>
                                ds.map((x) => (x.id === d.id ? { ...x, cachDoc: e.target.value } : x)),
                              )
                            }
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="nut nut--nho"
                            onClick={() => datBang((ds) => ds.filter((x) => x.id !== d.id))}
                          >
                            {vi.taoTuVanBan.xoaDong}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button
                  type="button"
                  className="nut"
                  onClick={() => datBang((ds) => [...ds, { id: `d${Date.now()}`, tu: '', cachDoc: '' }])}
                >
                  {vi.taoTuVanBan.themDong}
                </button>
              </section>
            </section>
          </div>

          <div className="nd-chan-form" data-vung="thanh-hanh-dong">
            {loiTao ? (
              <p role="alert" className="nd-loi">
                {loiTao}
              </p>
            ) : null}
            <Link
              to={banTinIdDich ? `/ban-tin/${banTinIdDich}/sua` : '/ban-tin/chon-loai'}
              className="nut nd-nut-quay-lai"
            >
              {t.soan.quayLai}
            </Link>
            {banTinIdDich ? null : (
              <button
                type="button"
                className="nut nut--chinh"
                data-hanh-dong="tao-ban-tin"
                disabled={!coVanBan || dangTao || dangGuiToanVan}
                onClick={() => void taoBanTin()}
              >
                {dangTao ? t.soan.dangLuu : t.soan.taoBanTin}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
